import React from 'react';
import { IErrorResponse } from 'src/utilities/shared-types';
import { SdkErrorResponse } from 'src/utilities/notifications';

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface BaseTableState {
  query?: string;
  page?: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: SortDirection;
}

export type SearchFilters = Record<
  string,
  string | string[] | boolean | number
>;

export type TableState<TFilters = SearchFilters> = BaseTableState & TFilters;

export enum TableAdapterStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

export interface TableAdapter<TData, TFilters = SearchFilters> {
  data: TData[];
  total: number;
  status: TableAdapterStatus;
  error?: IErrorResponse | SdkErrorResponse;
  fetch(state?: TableState<TFilters>): Promise<void>;
}

export type TableAdapterHook<TData, TFilters = SearchFilters> = (
  state?: TableState<TFilters>
) => TableAdapter<TData, TFilters>;

export interface TableContextValue<TData, TFilters = SearchFilters> {
  tableState: TableState<TFilters>;
  initialState: Partial<TableState<TFilters>>;
  data: TData[];
  total: number;
  status: TableAdapterStatus;
  error?: IErrorResponse | SdkErrorResponse;
  isLoading: boolean;
  isEmpty: boolean;
  isError: boolean;
  hasSearchQuery: boolean;

  setQuery(query: string): void;
  setFilters(filters: Partial<TFilters>): void;
  setPage(page: number): void;
  reset(): void;
  refresh(): Promise<void>;
}

export interface DataTableProviderProps<TData, TFilters> {
  adapter: TableAdapterHook<TData, TFilters>;
  initialState?: Partial<TableState<TFilters>>;
  children: React.ReactNode;
}

export const DEFAULT_TABLE_STATE: BaseTableState = {
  query: '',
  page: 1,
  pageSize: 25,
};
